// client/src/pages/Admin/ProjectManager/hooks/useProjects.ts
/**
 * USE PROJECTS HOOK
 * 
 * This hook manages project data fetching, state, and mutations.
 * It provides a clean interface for components to interact with projects.
 * 
 * @module ProjectManager/Hooks/useProjects
 */
import { useState, useCallback, useEffect } from 'react';

import {
    Project,
    ProjectStatus,
    CreateProjectDTO,
    UseProjectsReturn
} from '../types/project.types';

import {
    projectService,
    projectCache
} from '../services/projectService';

// ============================================
// HOOK IMPLEMENTATION
// ============================================

/**
 * useProjects hook for managing project data
 * 
 * @example
 * const { projects, loading, error, loadProjects, removeProject, updateProjectStatus } = useProjects();
 */
export const useProjects = (): UseProjectsReturn => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    /**
     * Load projects from the API
     * Uses cache if available to reduce API calls
     */
    const loadProjects = useCallback(async (): Promise<void> => {
        setLoading(true);
        setError('');

        try {
            // Check cache first
            const cachedProjects = projectCache.get('all');
            if (cachedProjects) {
                setProjects(Array.isArray(cachedProjects) ? cachedProjects : []);
                setLoading(false);
                return;
            }

            // Fetch from API
            const data = await projectService.fetchProjects();
            const projectArray = Array.isArray(data) ? data : [];
            setProjects(projectArray);
            
            // Cache the results
            if (projectArray.length > 0) {
                projectCache.set('all', projectArray as any);
            }
        } catch (err) {
            const errorMessage = err instanceof Error 
                ? err.message 
                : 'Failed to load projects';
            setError(errorMessage);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Remove a project by ID
     * Updates local state and cache
     */
    const removeProject = useCallback(async (id: string): Promise<void> => {
        if (!id) {
            throw new Error('Project ID is required');
        }

        try {
            await projectService.deleteProject(id);
            
            // Update local state
            setProjects(prev => prev.filter(project => project._id !== id));
            
            // Clear cache
            projectCache.remove('all');
            projectCache.remove(id);
        } catch (err) {
            const errorMessage = err instanceof Error 
                ? err.message 
                : 'Failed to delete project';
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Update a project's status
     * Updates local state and cache
     */
    const updateProjectStatus = useCallback(async (
        id: string,
        status: ProjectStatus
    ): Promise<void> => {
        if (!id) {
            throw new Error('Project ID is required');
        }
        if (!status) {
            throw new Error('Status is required');
        }

        try {
            const updated = await projectService.updateProject(id, { status });
            
            // Update local state
            setProjects(prev => 
                prev.map(project =>
                    project._id === id
                        ? { ...project, status, updatedAt: new Date() }
                        : project
                )
            );
            
            // Clear cache
            projectCache.remove('all');
            projectCache.remove(id);
        } catch (err) {
            const errorMessage = err instanceof Error 
                ? err.message 
                : 'Failed to update project status';
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Add a new project
     * Updates local state and cache
     */
    const addProject = useCallback(async (
    projectData: CreateProjectDTO
): Promise<Project> => {
    try {
        const newProject = await projectService.createProject(projectData);

        // Update local state
        setProjects(prev => [...prev, newProject]);

        // Clear cache
        projectCache.remove('all');

        return newProject;
    } catch (err) {
        const errorMessage = err instanceof Error
            ? err.message
            : 'Failed to create project';

        throw new Error(errorMessage);
    }
}, []);

    /**
     * Update a project
     * Updates local state and cache
     */
    const updateProject = useCallback(async (
    id: string,
    updates: Partial<CreateProjectDTO>
): Promise<Project> => {
    if (!id) {
        throw new Error('Project ID is required');
    }

    try {
        const updated = await projectService.updateProject(id, updates);

        // Update local state
        setProjects(prev =>
            prev.map(project =>
                project._id === id
                    ? { ...project, ...updated, updatedAt: new Date() }
                    : project
            )
        );

        // Clear cache
        projectCache.remove('all');
        projectCache.remove(id);

        return updated;
    } catch (err) {
        const errorMessage = err instanceof Error
            ? err.message
            : 'Failed to update project';

        throw new Error(errorMessage);
    }
}, []);

    // Load projects on mount
    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    return {
        projects,
        loading,
        error,
        loadProjects,
        removeProject,
        updateProjectStatus,
        addProject,
        updateProject
    };
};